import { useService } from '@nzyme/vue-ioc/useService.js';
import { toValue } from 'vue';
import { ActorClient } from '@sigma/actors-client/ActorClient.js';
import { ChatActor } from '@sigma/api-core/actors/ChatActor.js';
import { getChatNextEventSeq } from '@sigma/chat-common/utils/getChatNextEventSeq.js';
import { buildChatAnswerEvent } from './buildChatAnswerEvent.js';
/**
 * Encapsulates the optimistic answer-question flow shared between the doctor and patient chat
 * question components, and reused by {@link useChatComposer} for the free-text answer path.
 *
 * Pushes the optimistic event synchronously but defers `markRead` until the actor request
 * resolves, so the server-side read pointer only advances for events that were actually persisted.
 */
export function useAnswerChatQuestion(params) {
    // Resolved here (rather than injected) so call sites only describe *who* is answering and
    // *how* to mark read — the actor wiring is identical everywhere. Unit tests register a fake
    // ActorClient via the IoC container — see useAnswerChatQuestion.test.ts.
    const chatActorRef = useService(ActorClient).actor(ChatActor);
    return { submit };
    async function submit(answer, questionSeq) {
        const seq = getChatNextEventSeq(params.chat.events);
        const event = buildChatAnswerEvent({
            author: toValue(params.author),
            chatId: params.chatId,
            seq,
            questionSeq,
            answer,
        });
        // Push optimistic local event synchronously so the UI updates immediately.
        // `markRead` is intentionally deferred until after the actor request resolves —
        // persisting the read pointer for an event that never lands server-side would let
        // a later remote event reuse the same seq and be treated as already read,
        // suppressing unread notifications.
        params.chat.pushLocalEvent(event);
        try {
            await chatActorRef.requests.answerQuestion(params.chatId, { questionSeq, answer });
        }
        catch (error) {
            await params.chat.removeLocalEvents();
            throw error;
        }
        params.markRead(params.chatId, seq);
    }
}
//# sourceMappingURL=useAnswerChatQuestion.js.map